import {
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Dropdown,
  Menu,
  Checkbox,
  Radio,
} from '@arco-design/web-react'
import { IconRefresh, IconSearch, IconSettings } from '@arco-design/web-react/icon'
import type { MjLogsFilterState, MjColumnKey } from '@/hooks/useMjLogs'
import { MJ_COLUMN_KEYS } from '@/hooks/useMjLogs'

const STATUS_OPTIONS = [
  { label: '全部状态', value: '' },
  { label: '成功', value: 'SUCCESS' },
  { label: '失败', value: 'FAILURE' },
  { label: '进行中', value: 'IN_PROGRESS' },
  { label: '未启动', value: 'NOT_START' },
  { label: '已提交', value: 'SUBMITTED' },
  { label: '已取消', value: 'FAILURE_PAUSED' },
]

const COLUMN_LABELS: Record<MjColumnKey, string> = {
  submit_time: '提交时间',
  finish_time: '完成时间',
  channel_id: '渠道',
  action: '动作',
  mj_id: '任务 ID',
  prompt: '提示词',
  status: '状态',
  progress: '进度',
  image_url: '图片',
  fail_reason: '失败原因',
}

interface MjLogsFiltersProps {
  filters: MjLogsFilterState
  onChange: (patch: Partial<MjLogsFilterState>) => void
  onReset: () => void
  onRefresh: () => void
  isAdmin: boolean
  loading?: boolean
  visibleColumns: MjColumnKey[]
  onVisibleColumnsChange: (cols: MjColumnKey[]) => void
}

export default function MjLogsFilters({
  filters,
  onChange,
  onReset,
  onRefresh,
  isAdmin,
  loading,
  visibleColumns,
  onVisibleColumnsChange,
}: MjLogsFiltersProps) {
  const dateRange: [number, number] | undefined =
    filters.start_timestamp && filters.end_timestamp
      ? [filters.start_timestamp * 1000, filters.end_timestamp * 1000]
      : undefined

  return (
    <div className="space-y-3">
      {isAdmin && (
        <Radio.Group
          type="button"
          value={filters.scope}
          onChange={(v) => onChange({ scope: v })}
          options={[
            { label: '我的', value: 'self' },
            { label: '全部', value: 'all' },
          ]}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="按任务 ID 搜索"
          value={filters.mj_id}
          onChange={(v) => onChange({ mj_id: v })}
          style={{ width: 220 }}
          prefix={<IconSearch />}
          allowClear
        />

        <Select
          placeholder="选择状态"
          value={filters.status ?? ''}
          onChange={(v) => onChange({ status: v || undefined })}
          options={STATUS_OPTIONS}
          style={{ width: 160 }}
        />

        {isAdmin && filters.scope === 'all' && (
          <InputNumber
            placeholder="渠道 ID"
            value={filters.channel_id}
            onChange={(v) => onChange({ channel_id: v === undefined ? undefined : Number(v) })}
            style={{ width: 140 }}
            min={0}
          />
        )}

        <DatePicker.RangePicker
          showTime
          value={dateRange}
          onChange={(values) => {
            if (!values || !values[0] || !values[1]) {
              onChange({ start_timestamp: undefined, end_timestamp: undefined })
              return
            }
            onChange({
              start_timestamp: Math.floor(new Date(values[0] as string).getTime() / 1000),
              end_timestamp: Math.floor(new Date(values[1] as string).getTime() / 1000),
            })
          }}
          style={{ width: 360 }}
        />

        <div className="flex-1" />

        <Button onClick={onReset}>重置</Button>
        <Button icon={<IconRefresh />} onClick={onRefresh} loading={loading}>
          刷新
        </Button>

        <Dropdown
          trigger="click"
          droplist={
            <Menu style={{ minWidth: 180 }}>
              {MJ_COLUMN_KEYS.map((key) => (
                <Menu.Item key={key}>
                  <Checkbox
                    checked={visibleColumns.includes(key)}
                    onChange={(checked) => {
                      if (checked) {
                        onVisibleColumnsChange(
                          MJ_COLUMN_KEYS.filter(
                            (k) => visibleColumns.includes(k) || k === key
                          )
                        )
                      } else {
                        onVisibleColumnsChange(visibleColumns.filter((k) => k !== key))
                      }
                    }}
                  >
                    {COLUMN_LABELS[key]}
                  </Checkbox>
                </Menu.Item>
              ))}
            </Menu>
          }
        >
          <Button icon={<IconSettings />}>列</Button>
        </Dropdown>
      </div>
    </div>
  )
}
